import * as React from 'react';
import RouteHelpers from 'helpers/routes';
import useAsync from 'hooks/useAsync';
import { Navigate } from 'react-router-dom';
import PageLoader from 'components/PageLoader';
import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { Assessment } from 'types/api/envScales/Assessment';
import { getAgeLevel } from 'actions/envScales/assessmentTemplates';
import { getAssessment } from 'actions/envScales/assessments';

interface MeasureContextValue {
  setAssessment: (assessment: Assessment) => void;

  /** Refetches assessment and age level, keeping current data on screen. */
  refresh: () => void;

  /** `true` while a refresh is in flight. */
  isRefreshing: boolean;

  assessment: Assessment;
  ageLevel: AgeLevel;
}

const MeasureContext = React.createContext<MeasureContextValue | null>(null);
MeasureContext.displayName = 'MeasureContext';

interface MeasureProviderProps {
  /** Assessment ID. */
  assessmentId: number;

  /** React Node. */
  children: React.ReactNode | ((value: MeasureContextValue) => React.ReactNode);

  /** If `true`, the CLASS assessment will contain all cycle scores.  */
  expanded?: boolean;
}

export function MeasureProvider({
  assessmentId,
  children,
  expanded
}: MeasureProviderProps) {
  const { run: assessmentRun, ...assessmentReq } = useAsync();
  const { run: ageLevelRun, ...ageLevelReq } = useAsync();

  const load = React.useCallback(() => {
    assessmentRun(getAssessment(assessmentId, { expanded }), {
      onSuccess: data => {
        ageLevelRun(getAgeLevel(data.assessment_template_id, data.age_level));
      }
    });
  }, [assessmentRun, ageLevelRun, assessmentId, expanded]);

  React.useEffect(() => {
    load();
  }, [load]);

  const assessment: Assessment | null = assessmentReq.data;
  const ageLevel: AgeLevel | null = ageLevelReq.data;

  /**
   * Only the first load blocks: `useAsync` keeps its data while pending, so a
   * refresh resolves behind the current screen and a failed one changes
   * nothing.
   */
  if (!assessment || !ageLevel) {
    if (assessmentReq.isError) {
      return <Navigate replace to={RouteHelpers.getPath('measure')} />;
    }

    return <PageLoader />;
  }

  const value: MeasureContextValue = {
    setAssessment: assessmentReq.setData,
    refresh: load,
    isRefreshing: assessmentReq.isLoading || ageLevelReq.isLoading,
    assessment,
    ageLevel
  };

  return (
    <MeasureContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </MeasureContext.Provider>
  );
}

// Typescript function overloads
type UseMeasureProps = { skipContextCheck: boolean };
export function useMeasure(): MeasureContextValue;
export function useMeasure(props: UseMeasureProps): MeasureContextValue | null;

export function useMeasure(props?: UseMeasureProps) {
  const context = React.useContext(MeasureContext);
  const { skipContextCheck } = props || {};

  if (!context && !skipContextCheck) {
    throw new Error('useMeasure must be used within <MeasureProvider />');
  }

  return context;
}
